import type { DemoBundle, PublishIssue } from "./types";
import { tokensIn } from "./personalize";
import { pathScreens } from "./tour";
import type { Step } from "./types";

/** What a viewer sees of a step: its media and everything drawn on it (not its id, so a path's copy compares equal). */
const looks = (s: Step) => JSON.stringify([s.imageKey, s.captureKey, s.mediaType, s.caption, s.autoAdvanceMs, s.textCard ?? null, s.callout ?? null,
  s.focus ?? null, s.redactions ?? [], s.chapterTitle ?? null, s.hotspots.map((h) => [h.x, h.y, h.w, h.h, h.action, h.tooltipText, h.linkUrl, h.pulse])]);

/** Checks shown in the editor's Publish step. Errors block publishing; warnings do not. */
export function validateDemo(b: DemoBundle): PublishIssue[] {
  const issues: PublishIssue[] = [];
  const defaults = pathScreens(b, null);
  if (defaults.length === 0) {
    issues.push({ level: "error", message: "The default path has no screens. Upload at least one screenshot." });
  }

  const paths: { label: string; outcomeId: string | null }[] = [{ label: "Default path", outcomeId: null }];
  const answerTags = new Set(
    b.questions.flatMap((q) => q.options.flatMap((o) => (o.attached_tags ?? []).map((t) => t.trim()))).filter(Boolean),
  );

  if (b.demo.surveyEnabled) {
    if (b.questions.length === 0) {
      issues.push({ level: "error", message: "The survey is turned on but has no questions. Add a question or turn the survey off." });
    }
    for (const q of b.questions) {
      if (!q.prompt.trim()) issues.push({ level: "error", message: `Question ${q.orderIndex + 1} has no text.` });
      if (q.answerType !== "short_text" && q.options.length < 2) {
        issues.push({ level: "error", message: `Question "${q.prompt || q.orderIndex + 1}" needs at least two answers.` });
      }
    }
  }

  // Every path is reachable by a link (?path=), so each one is checked, survey or not.
  const defaultLooks = JSON.stringify(defaults.map(looks));
  for (const o of b.outcomes ?? []) {
    paths.push({ label: o.name, outcomeId: o.id });
    const seq = pathScreens(b, o.id);
    const tags = (o.tags ?? []).map((t) => t.trim()).filter(Boolean);
    if (seq.length === 0) {
      issues.push({ level: "error", message: `"${o.name}" has no screens. Add screens to it in the Studio, or delete the path.` });
    } else if (JSON.stringify(seq.map(looks)) === defaultLooks) {
      issues.push({ level: "warning", message: `"${o.name}" shows the same screens as the default path, unchanged. A path only needs to exist if its screens differ.` });
    }
    if (tags.length === 0) {
      issues.push({ level: "warning", message: `"${o.name}" has no routing tags, so the survey never sends a viewer there (a ?path= link still can).` });
    } else if (b.demo.surveyEnabled && !tags.some((t) => answerTags.has(t))) {
      issues.push({ level: "warning", message: `"${o.name}" routes on ${tags.map((t) => `#${t}`).join(", ")}, but no survey answer gives that tag, so no viewer is sent there.` });
    }
  }

  const stepIds = new Set(b.steps.map((s) => s.id));
  const checked = new Set<string>();
  for (const p of paths) {
    const seq = pathScreens(b, p.outcomeId);
    seq.forEach((s, i) => {
      if (checked.has(s.id)) return;
      checked.add(s.id);
      const isLast = i === seq.length - 1;
      // A callout or text card has its own button that moves on, so it doesn't need a hotspot or timer.
      const hasCallout = !!s.callout && (s.callout.showButton !== false && s.callout.showNextButton !== false) && !!(s.callout.title.trim() || s.callout.body.trim());
      const hasTextCard = !!s.textCard;
      if (!isLast && s.hotspots.length === 0 && !s.autoAdvanceMs && !hasCallout && !hasTextCard) {
        issues.push({ level: "warning", message: `${p.label}, screen ${i + 1} has no hotspot and no auto-advance. Viewers can only use the Next button.` });
      }
      if (s.textCard?.buttonUrl && !/^https?:\/\//.test(s.textCard.buttonUrl)) {
        issues.push({ level: "error", message: `${p.label}, screen ${i + 1} has a button link without a valid http(s) URL.` });
      }
      if (s.textCard?.secondaryUrl && !/^https?:\/\//.test(s.textCard.secondaryUrl)) {
        issues.push({ level: "error", message: `${p.label}, screen ${i + 1} has a secondary button link without a valid http(s) URL.` });
      }
      for (const h of s.hotspots) {
        if (h.action === "goto_step" && (!h.targetStepId || !stepIds.has(h.targetStepId))) {
          issues.push({ level: "warning", message: `${p.label}, screen ${i + 1} has a "Go to screen" hotspot with no target. It will act as Next.` });
        }
        if (h.action === "open_link" && !/^https?:\/\//.test(h.linkUrl)) {
          issues.push({ level: "error", message: `${p.label}, screen ${i + 1} has a link hotspot without a valid http(s) URL.` });
        }
      }
    });
  }
  // Personalisation: a variable without a fallback renders blank for viewers whose link doesn't set it.
  const bare = new Set<string>();
  const scan = (text: string) => tokensIn(text).forEach((t) => { if (!t.hasFallback) bare.add(t.key); });
  [b.demo.surveyIntro, b.demo.ctaLabel, b.demo.leadTitle].forEach(scan);
  b.questions.forEach((q) => { scan(q.prompt); q.options.forEach((o) => scan(o.label)); });
  b.steps.forEach((s) => {
    scan(s.caption);
    if (s.callout) [s.callout.title, s.callout.body, s.callout.button].forEach(scan);
    if (s.textCard) {
      [s.textCard.title, s.textCard.body, s.textCard.badge, s.textCard.buttonLabel, s.textCard.secondaryLabel]
        .filter((x): x is string => !!x)
        .forEach(scan);
    }
    s.hotspots.forEach((h) => scan(h.tooltipText));
  });
  if (bare.size) {
    const list = [...bare].map((k) => `{{${k}}}`).join(", ");
    issues.push({ level: "warning", message: `${list} has no fallback, so it's blank when the link doesn't include it. Write {{${[...bare][0]}|your team}} to set one.` });
  }
  if (b.demo.leadGate === "step" && defaults.length > 0 && b.demo.leadGateStep > defaults.length) {
    issues.push({ level: "warning", message: `The lead form is set to appear before screen ${b.demo.leadGateStep}, but the default path has ${defaults.length}. It will show before the last screen.` });
  }

  return issues;
}
